import React from 'react';
import styled from 'styled-components';

import { UserAbonnementStatusEnum } from '~/api/account/accountApiTypes';
import { Button } from '~/atomic/atom/Button';
import { Description } from '~/atomic/atom/Description';
import { useHaveAbonnementDiscount } from '~/atomic/page/bookSubscriptions/abonnement.selector';
import {
  getUserAbonnementError,
  isHaveUserAbonnementErrorFunc,
} from '~/atomic/page/bookSubscriptions/abonnementPage.data';
import { AbonnementPrompt } from '~/atomic/page/bookSubscriptions/AbonnementPrompt';
import { PageTitleStyle, TextSmallStyle } from '~/atomic/Typography';
import { AbonnementProgress } from '~/feature/abonnement/AbonnementProgress';
import { isLoggedInSelector } from '~/feature/user/isLoggedInSelector';
import { getAbonnementCharacter } from '~/graphql/books/factory/abonement/AbonementFactory';
import {
  AbonnementType,
  AbonnementTypeEnum,
  UserAbonnement,
} from '~/graphql/books/factory/abonement/AbonnementFactoryTypes';
import { dateTimeShortestRU } from '~/lib/dateTimeFormatter';
import { lessThan } from '~/lib/mediaQuery';
import { useAppSelector } from '~/store';

interface AbonnementPageCardProps extends React.HTMLAttributes<HTMLDivElement> {
  abonnement: AbonnementType;
  userAbonnement: UserAbonnement;
  connectAbonnement: (abonnement: AbonnementType) => void;
  enableAbonnementAutoRenewal: () => void;
  disableAbonnementAutoRenewal: () => void;
}
export const AbonnementPageCard = ({
  abonnement,
  userAbonnement,
  connectAbonnement,
  enableAbonnementAutoRenewal,
  disableAbonnementAutoRenewal,
  ...props
}: AbonnementPageCardProps) => {
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const isCurrentAbonnement = userAbonnement?.abonnementType?.type === abonnement.type;
  if (!(abonnement?.type === AbonnementTypeEnum.Seeker || isCurrentAbonnement)) {
    return (<> </>);
  }

  const isHaveUserAbonnementError = isHaveUserAbonnementErrorFunc(userAbonnement);
  const userAbonnementError = getUserAbonnementError(userAbonnement);
  const isHaveError = userAbonnement?.abonnementType.type === abonnement.type && isHaveUserAbonnementError;

  const isHaveUserAbonnement = userAbonnement?.status === UserAbonnementStatusEnum.Active;
  const isActivatedCurrentAbonnement = isHaveUserAbonnement
    ? userAbonnement?.abonnementType.type === abonnement.type && !isHaveError
    : false;

  const isShowAutoRenewalButtons = isHaveUserAbonnement && !isHaveError && (userAbonnement?.switchAbonnementType
    ? userAbonnement.switchAbonnementType
    : userAbonnement?.abonnementType?.type) === abonnement.type;

  const connectAbonnementHandler = () => {
    connectAbonnement(abonnement);
  };

  const abonnementPrice = abonnement?.discountedPrice ?? abonnement?.price;
  const haveDiscount = useHaveAbonnementDiscount(abonnement, isLoggedIn);

  return (
    <>
      <SCBookSubscriptionsCard {...props}>
        <SCHeader>
          <SCControls>
            {!isHaveUserAbonnement && (
              <SCConnectButtonWrapper>
                <SCConnectButton
                  size="large"
                  type="secondary"
                  onClick={connectAbonnementHandler}
                >
                  Купить абонемент за
                  {' '}
                  {abonnementPrice}
                  {' '}
                  ₽
                </SCConnectButton>
                {haveDiscount && (
                  <SCDescription>
                    Первый месяц, далее 299 ₽ в месяц
                  </SCDescription>
                )}
              </SCConnectButtonWrapper>
            )}
            {isShowAutoRenewalButtons && userAbonnement.isAutoRenewalEnabled ? (
              <SCUnConnectButton
                size="large"
                type="error"
                onClick={disableAbonnementAutoRenewal}
              >
                Отменить автопродление
              </SCUnConnectButton>
            ) : null}
            {isShowAutoRenewalButtons && !userAbonnement.isAutoRenewalEnabled ? (
              <SCUnConnectButton
                size="large"
                type="secondary"
                onClick={enableAbonnementAutoRenewal}
              >
                Включить автопродление
              </SCUnConnectButton>
            ) : null}
            {isHaveError ? (
              <SCErrorMessageWrapper>
                <SCErrorMessage>
                  Не получилось продлить абонемент:
                  <br />
                  {userAbonnementError}
                </SCErrorMessage>
              </SCErrorMessageWrapper>
            ) : null}
          </SCControls>
          <SCTitle>
            {isActivatedCurrentAbonnement ? `Абонемент до ${dateTimeShortestRU(new Date(userAbonnement?.shouldExpireAt))}` : null}
            {userAbonnement?.discount?.isActive && ` (продление за ${userAbonnement.discount.price}₽)`}
          </SCTitle>
          {isActivatedCurrentAbonnement ? (
            <SCAbonnementProgressWrapper>
              <AbonnementProgress
                height={15}
                progressRead={userAbonnement?.characterConsumptionAsPercent}
                isShowText
              />
            </SCAbonnementProgressWrapper>
          ) : null}
        </SCHeader>
        <SCInfo>
          <Description
            data={[
              <>
                Общий объем чтения не должен превышать
                {' '}
                {getAbonnementCharacter(abonnement.type)}
                {' '}
                тыс. зн платных глав
                {' '}
                <AbonnementPrompt />
              </>,
              'Абонемент продляется автоматически по мере завершения. Вы можете отменить автопродление в любой момент.',
            ]}
          />
        </SCInfo>
      </SCBookSubscriptionsCard>
    </>
  );
};

const SCBookSubscriptionsCard = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 0;
  flex-direction: column;
`;

const SCInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 24px 0;
  max-width: 600px;

  ${lessThan('md')} {
    padding: 24px 30px;
  }

  ${lessThan('xs')} {
    padding: 16px 16px 0 16px;
  }
`;

const SCTitle = styled.h2`
  ${PageTitleStyle};
`;

const SCControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  flex-shrink: 0;
  max-width: 310px;
  width: 100%;

  > *:not(:last-child) {
    margin-bottom: 12px;
  }

  ${lessThan('md')} {
    align-items: flex-start;
  }
`;

const SCConnectButton = styled(Button)`
  width: 100%;

  > * {
    margin-right: 12px;
  }
`;

const SCUnConnectButton = styled(Button)`
  width: 100%;

  > * {
    margin-right: 12px;
  }
`;

const SCErrorMessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SCErrorMessage = styled.span`
  font-family: var(--main-font);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: var(--error-color);
`;

const SCHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
  
  ${lessThan('md')} {
    padding: 20px 30px;
    flex-direction: column;
    max-width: 360px;
    gap: 20px;
  }

  ${lessThan('xs')} {
    padding: 16px;
    max-width: 332px;
  }
`;

const SCAbonnementProgressWrapper = styled.div`
  max-width: 300px;
  width: 100%;
`;

const SCDescription = styled.span`
  ${TextSmallStyle};
  color: var(--black-color);
`;

const SCConnectButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;
